import { useEffect, useRef, useState, useMemo } from 'react'
import { Col, Container, Form, Row, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik } from 'formik'
import * as Yup from 'yup'
import shallow from 'zustand/shallow'
import { Layout } from 'layouts/Layout'
import { BreadcrumbMenu } from 'layouts/BreadcrumbMenu'
import { HTMLText } from 'components/Share/HtmlText'
import ScrollButton from 'components/ScrollButton/ScrollButton'
import Select from 'components/Select/Select'
import { EmailConfirmation } from 'components/MyProfile/EmailConfirmation'
import { OverlayLoader } from 'components/Loader'
import { DismissableAlert } from 'components/DismissableAlert/DismissableAlert'
import AddLocationPopover from 'components/AccountSettingsComponent/AccountSettingPopover'
import {
  AccountSettingStyled,
  AddLocationLink,
  CancelButton,
  CarfaxSignOutLink,
  GroupButtonStyled,
  MainContainer,
  SettingsSection,
  SectionTitle,
  UpdateButton,
  Title
} from './AccountSettings.styled'
import { LinkBaseStyled } from 'layouts/LoginLayout.styled'
import { useGlobalStore } from 'store/useGlobalStore'
import { useFetch } from 'hooks/useFetch'
import {
  getAccountSettings,
  logoutCarfax,
  saveAddress,
  saveUserProfile,
  getBuyersByCurrentUser
} from 'apis/accountSettingsApis'
import { IAddressRequest } from 'types/accountTypes'
import { IUserProfile, ISaveUserProfileRequest } from 'types/userTypes'
import { AddressTypes, ISystemSetting } from 'types/baseTypes'
import { MultiLanguageCode } from 'common/languageCodes'
import { getGateReleaseNotificationEmails, getPurchaseNotificationEmails } from 'apis/userApis'
import { validationSchema, PAYMENT_TYPE_FLOOR_PLAN, IMessage, INITIAL_ADDRESS_VALUES } from 'types/accountSettingsTypes'

interface IFormValues extends IUserProfile {
  PurchaseEmails: string[]
  SoldEmails: string[]
}

export const AccountSettings = () => {
  const intl = useIntl()
  const { loading, data, refetch } = useFetch(() => getAccountSettings())
  const addLocationRef = useRef(null)

  const [userClaims, getLocalText, vendor, getSystemSetting, isInRule] = useGlobalStore(
    (state) => [state.userClaims, state.getLocalText, state.vendor, state.getSystemSetting, state.isInRule],
    shallow
  )

  const [showPopover, setShowPopover] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [userProfile, setUserProfile] = useState<IUserProfile | null>(null)
  const [otherError, setOtherError] = useState<string>('')
  const [buyerError, setBuyerError] = useState<string>('')
  const [isCarfaxLoggedIn, setIsCarfaxLoggedIn] = useState(false)
  const [message, setMessage] = useState<IMessage>({ isSuccess: true, message: '' })
  const [formikRef, setFormikRef] = useState<any>(null)
  const { data: purchaseNotificationEmails } = useFetch(() => getPurchaseNotificationEmails())
  const { data: gateReleaseNotificationEmails } = useFetch(() => getGateReleaseNotificationEmails())

  const showAuctionAcessRegistration = getSystemSetting('AUCTION_ACCESS_REGISTER_ROLES') as ISystemSetting
  const showSiteNotificationEmails = getSystemSetting('SITE_NOTIFICATION_EMAIL_SECTION_SETTINGS') as ISystemSetting

  useEffect(() => {
    if (data) {
      setUserProfile(data.UserProfile)
      // TODO: Set CARFAX login status from data if available
      setIsCarfaxLoggedIn(data?.IsCarFaxLogined || false)
    }
  }, [data])

  const handleLaunchAuctionAccess = () => {
    const auctionAccessUrl = (getSystemSetting('AUCTION_ACCESS_URL') as ISystemSetting)?.ValueString || '/account#'
    window.open(auctionAccessUrl, '_blank')
  }

  const handleCarfaxLogout = async () => {
    if (!isCarfaxLoggedIn) return

    setIsSaving(true)

    try {
      const success = await logoutCarfax()

      if (success) {
        setIsCarfaxLoggedIn(false)
        const carfaxLogoutLink = data?.CarFaxLogoutLink || 'https://auth.carfax.com/v2/logout'
        window.open(
          carfaxLogoutLink,
          '_blank',
          'directories=no,SectionTitlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=yes,width=960,height=540'
        )
      } else {
        console.error('Failed to logout of CARFAX')
      }
    } catch (error) {
      console.error('Error logging out of CARFAX:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const [oveBuyerList, setOveBuyerList] = useState<any[]>([])

  const buyerOptions = useMemo(() => {
    return data?.BuyerList?.map((x) => ({ value: x.ID, label: x.Description })) || []
  }, [data?.BuyerList])

  const loadBuyers = async () => {
    if (isInRule('OVE Colisting') && userProfile) {
      const currentBuyer = userProfile.CurBuyer
      try {
        const oveBuyers = await getBuyersByCurrentUser()
        console.log('Raw OVE buyers from API:', oveBuyers)

        const processedBuyers =
          oveBuyers?.map((buyer) => ({
            ID: buyer.ID || buyer.id || 0,
            Selected: buyer.Selected || buyer.selected || false
          })) || []

        setOveBuyerList(processedBuyers)
        setUserProfile((prev) => (prev ? { ...prev, CurBuyer: currentBuyer } : null))
      } catch (error) {
        console.error('Error loading OVE buyers:', error)
      }
    } else {
      console.log('User not in OVE Colisting rule or userProfile not available')
    }
  }

  useEffect(() => {
    if (userProfile) {
      loadBuyers()
    }
  }, [userProfile])

  const clearAvailableBuyer = () => {
    setOveBuyerList([])
  }

  const handleOpenAddressPopover = () => setShowPopover(true)

  const handleSaveAddress = async (addressData: any) => {
    try {
      setShowPopover(false)
      setIsLoading(true)

      const address = {
        AddressLineOne: addressData.address,
        AddressLineTwo: addressData.address2 || '',
        Town: addressData.city,
        StateId: addressData?.stateCode || null,
        PostCode: addressData.zipcode,
        CountryId: addressData?.countryCode || null,
        AddressTypeId: AddressTypes.DELIVERY
      }

      const request: IAddressRequest = {
        Address: address,
        SaveToBuyer: true
      }

      const result = await saveAddress(request)

      if (result > 0) {
        const stateCode = addressData.state?.label || ''
        const newAddressDescription = `${address.AddressLineOne}${
          address.AddressLineTwo ? `, ${address.AddressLineTwo}` : ''
        }, ${address.Town}, ${stateCode} ${address.PostCode}`

        setMessage({ isSuccess: true, message: 'Address saved successfully!' })
        console.log('Address saved successfully with ID:', result)
        console.log('New address description:', newAddressDescription)

        await refetch()

        if (formikRef) {
          formikRef.setFieldValue('DeliveryLocationID', result)
        }
      }
    } catch (error) {
      console.error('Error saving address:', error)
      setMessage({ isSuccess: false, message: 'Failed to save address. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSaveUserProfile = async (values: IFormValues) => {
    if (!userProfile || !data) {
      setMessage({ isSuccess: false, message: 'Profile data not loaded. Please refresh the page and try again.' })
      return
    }

    setIsLoading(true)
    setMessage({ isSuccess: true, message: '' })

    setOtherError('')
    setBuyerError('')

    const isValid = await validateFormValues(values)
    if (!isValid) {
      setIsLoading(false)
      return
    }

    try {
      setIsSaving(true)
      const { PurchaseEmails, SoldEmails, ...userProfileData } = values

      const saveRequest: ISaveUserProfileRequest = {
        DeliveryModeID: userProfileData.DeliveryModeID,
        curAccount: values.curAccount,
        DeliveryModeCode: userProfileData.DeliveryModeCode,
        DeliveryLocationID: userProfileData.DeliveryLocationID,
        CurBuyer: userProfileData.CurBuyer,
        DeliveryLocationCode: userProfileData.DeliveryLocationCode,
        DeliveryMethodID: userProfileData.DeliveryMethodID,
        UserID: userProfileData.UserID,
        IsReserveMatch: userProfileData.IsReserveMatch,
        TransportCompanyID: userProfileData.TransportCompanyID,
        DefaultContactID: userProfileData.DefaultContactID,
        DefaultPaymentTypeID: userProfileData.DefaultPaymentTypeID,
        PaymentTypeOther: userProfileData.PaymentTypeOther,
        DeliveryColleciton: userProfileData.DeliveryColleciton,
        AssingedBuyerIds: oveBuyerList.length > 0 ? oveBuyerList : [],
        PurchaseConfirmationEmail: PurchaseEmails.join(';'),
        GateReleaseConfirmationEmail: SoldEmails.join(';')
      }

      const result = await saveUserProfile(saveRequest)
      setUserProfile((prev) => (prev ? { ...prev, ID: result } : null))

      setMessage({ isSuccess: true, message: 'Account settings updated successfully!' })

      clearAvailableBuyer()

      if (oveBuyerList.length > 0) {
        window.location.reload()
        return
      }
    } catch (error: any) {
      console.error('Error saving profile:', error)

      if (error?.Success === false && error?.ErrorMessages) {
        const errorMessage =
          error.ErrorMessages.length > 0
            ? error.ErrorMessages.join('. ')
            : 'An error occurred while updating your account settings'
        setMessage({ isSuccess: false, message: errorMessage })
      } else {
        const errorMessage = error?.message || 'Network error occurred. Please try again.'
        setMessage({ isSuccess: false, message: errorMessage })
      }
    } finally {
      setIsSaving(false)
      setIsLoading(false)
    }
  }

  const validateFormValues = async (values: IFormValues): Promise<boolean> => {
    try {
      await validationSchema.validate(values, { abortEarly: false })
      setOtherError('')
      setBuyerError('')
      return true
    } catch (error) {
      if (
        values.DefaultPaymentTypeID === PAYMENT_TYPE_FLOOR_PLAN &&
        (!values.PaymentTypeOther || values.PaymentTypeOther.trim() === '')
      ) {
        document.getElementById('ofp-name')?.focus()
      }
      return false
    }
  }

  return (
    <Layout title="Account Settings">
      <AccountSettingStyled>
        <Row>
          <Col>
            <BreadcrumbMenu />
          </Col>
        </Row>
        <Row>
          <Col>
            <Title>
              <HTMLText value="Account Settings" />
            </Title>
          </Col>
        </Row>
        {(isLoading || loading) && <OverlayLoader />}
        {!loading && (
          <Formik<IFormValues>
            initialValues={{
              DeliveryModeID: userProfile?.DeliveryModeID || 0,
              curAccount: userProfile?.curAccount || 0,
              DeliveryModeCode: userProfile?.DeliveryModeCode || '',
              DeliveryLocationID: userProfile?.DeliveryLocationID || 0,
              CurBuyer: userProfile?.CurBuyer || 0,
              DeliveryLocationCode: userProfile?.DeliveryLocationCode || '',
              DeliveryMethodID: userProfile?.DeliveryMethodID || 0,
              UserID: userProfile?.UserID || 0,
              IsReserveMatch: userProfile?.IsReserveMatch || false,
              SecretQuestion1: userProfile?.SecretQuestion1 || 0,
              SecretAnswer1: userProfile?.SecretAnswer1 || '',
              SecretQuestion2: userProfile?.SecretQuestion2 || 0,
              SecretAnswer2: userProfile?.SecretAnswer2 || '',
              TransportCompanyID: userProfile?.TransportCompanyID || 0,
              DefaultContactID: userProfile?.DefaultContactID || 0,
              DefaultPaymentTypeID: userProfile?.DefaultPaymentTypeID || 0,
              PaymentTypeOther: userProfile?.PaymentTypeOther || '',
              DeliveryColleciton: userProfile?.DeliveryColleciton || '',
              AssingedBuyerIds: userProfile?.AssingedBuyerIds ?? { ID: 0, Selected: false },
              PurchaseConfirmationEmail: userProfile?.PurchaseConfirmationEmail || '',
              GateReleaseConfirmationEmail: userProfile?.GateReleaseConfirmationEmail || '',
              PurchaseEmails: purchaseNotificationEmails ? purchaseNotificationEmails?.split(';') : [],
              SoldEmails: gateReleaseNotificationEmails ? gateReleaseNotificationEmails?.split(';') : []
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => handleSaveUserProfile(values)}
            enableReinitialize
          >
            {(formik) => {
              if (!formikRef) {
                setFormikRef(formik)
              }

              return (
                <Form noValidate onSubmit={formik.handleSubmit}>
                  {message?.message?.length > 0 && (
                    <Container>
                      <Row className="mt-3">
                        <Col xs={12}>
                          <DismissableAlert
                            variant={message.isSuccess ? 'success' : 'danger'}
                            message={message.message}
                            onClose={() => setMessage({ isSuccess: true, message: '' })}
                            autoDismissable={message.isSuccess ? 5000 : 0}
                          />
                        </Col>
                      </Row>
                    </Container>
                  )}

                  <MainContainer>
                    <Container>
                      <Col>
                        {data?.BuyerList && data.BuyerList.length === 1 && (
                          <p className="roleMain">
                            <span>{getLocalText('PAYMENT_DEFAULT_MESSAGE', 'Payment Default Message')}</span>
                          </p>
                        )}
                        {data?.BuyerList && data.BuyerList.length === 1 && userProfile && (
                          <>
                            <SettingsSection>
                              <SectionTitle>
                                {getLocalText('DEFAULT_PAYMENT_METHOD_OPTIONS', 'Default Payment Method Options')}
                              </SectionTitle>
                              <div className="row">
                                <div className="form-group select col-sm-6">
                                  <div className="d-flex justify-content-between align-items-baseline">
                                    <label className="control-label" htmlFor="pm-method">
                                      {getLocalText('DD_PAYMENT_METHOD', 'Payment Method')}
                                    </label>

                                    <div className="alt-links">
                                      <a
                                        href="/account#paymentInformation"
                                        onClick={(e) => {
                                          e.preventDefault()
                                          window.location.href = '/account#paymentInformation'
                                        }}
                                      >
                                        {getLocalText('EDIT_PAYMENT_METHOD', 'Edit payment method')}
                                      </a>
                                    </div>
                                  </div>
                                  <Select
                                    id="pm-method"
                                    name="DefaultPaymentTypeID"
                                    options={
                                      data?.PaymentTypes?.map((x) => ({ value: x.ID, label: x.Description })) || []
                                    }
                                    value={
                                      data?.PaymentTypes?.find((pt) => pt.ID === formik.values.DefaultPaymentTypeID)
                                        ? {
                                            value: formik.values.DefaultPaymentTypeID,
                                            label: data?.PaymentTypes.find(
                                              (pt) => pt.ID === formik.values.DefaultPaymentTypeID
                                            )?.Description
                                          }
                                        : null
                                    }
                                    onChange={(selectedOption: any) => {
                                      if (selectedOption) {
                                        formik.setFieldValue('DefaultPaymentTypeID', selectedOption.value)
                                      }
                                    }}
                                    isClearable={false}
                                  />
                                  {formik.values.DefaultPaymentTypeID === PAYMENT_TYPE_FLOOR_PLAN && (
                                    <div>
                                      <br />
                                      <label className="control-label" htmlFor="ofp-name">
                                        Floor Plan Name<sup title="Required">*</sup>
                                      </label>
                                      <input
                                        className={`form-control ${
                                          formik.touched.PaymentTypeOther && formik.errors.PaymentTypeOther
                                            ? 'is-invalid'
                                            : ''
                                        }`}
                                        id="ofp-name"
                                        name="PaymentTypeOther"
                                        type="text"
                                        value={formik.values.PaymentTypeOther || ''}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        required
                                      />
                                      {formik.touched.PaymentTypeOther && formik.errors.PaymentTypeOther && (
                                        <span className="has-error">{formik.errors.PaymentTypeOther}</span>
                                      )}
                                      {otherError && <span className="has-error">{otherError}</span>}
                                      <br />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </SettingsSection>
                            {data?.AllowThirdParty === true && (
                              <SettingsSection>
                                <Row>
                                  <Col>
                                    <SectionTitle>
                                      {getLocalText('DEFAULT_DELIVERY_OPTIONS', 'Default Delivery Options')}
                                    </SectionTitle>
                                  </Col>
                                </Row>
                                <Row>
                                  <Col>
                                    <div className="row">
                                      <div className="form-group select col-sm-6 col-md-3">
                                        <label className="control-label" htmlFor="do-provider">
                                          {getLocalText('TRANSPORTATION_PROVIDER', 'Transportation Provider')}
                                        </label>
                                        <Select
                                          id="do-provider"
                                          name="TransportCompanyID"
                                          options={
                                            data?.DeliveryColleciton?.map((x) => ({
                                              value: x.ID,
                                              label: x.Description
                                            })) || []
                                          }
                                          value={
                                            data?.DeliveryColleciton?.find(
                                              (dc) => dc.ID === formik.values.TransportCompanyID
                                            )
                                              ? {
                                                  value: formik.values.TransportCompanyID,
                                                  label: data?.DeliveryColleciton.find(
                                                    (dc) => dc.ID === formik.values.TransportCompanyID
                                                  )?.Description
                                                }
                                              : null
                                          }
                                          onChange={(selectedOption: any) => {
                                            if (selectedOption) {
                                              formik.setFieldValue('TransportCompanyID', selectedOption.value)
                                            }
                                          }}
                                          isClearable={false}
                                        />
                                      </div>
                                      {data?.DeliveryTypes && data.DeliveryTypes.length > 1 && (
                                        <div className="form-group select col-sm-6 col-md-3">
                                          <label className="control-label" htmlFor="do-type">
                                            {getLocalText('DEFAULT_DELIVERY_TYPE', 'Default Delivery Type')}
                                          </label>
                                          <Select
                                            id="do-type"
                                            name="DeliveryModeID"
                                            options={data?.DeliveryTypes.map((x) => ({
                                              value: x.ID,
                                              label: x.Description
                                            }))}
                                            value={
                                              data?.DeliveryTypes.find((dt) => dt.ID === formik.values.DeliveryModeID)
                                                ? {
                                                    value: formik.values.DeliveryModeID,
                                                    label: data?.DeliveryTypes.find(
                                                      (dt) => dt.ID === formik.values.DeliveryModeID
                                                    )?.Description
                                                  }
                                                : null
                                            }
                                            onChange={(selectedOption: any) => {
                                              if (selectedOption) {
                                                formik.setFieldValue('DeliveryModeID', selectedOption.value)
                                              }
                                            }}
                                            isClearable={false}
                                          />
                                        </div>
                                      )}
                                      <div className="form-group select col-sm-12 col-md-6">
                                        <div className="d-flex justify-content-between align-items-baseline">
                                          <label className="control-label" htmlFor="do-location">
                                            {getLocalText('DELIVERY_LOCATION', 'Delivery Location')}
                                          </label>
                                          <div className="alt-links">
                                            <OverlayTrigger
                                              trigger="click"
                                              placement="bottom"
                                              show={showPopover}
                                              overlay={
                                                <AddLocationPopover
                                                  initialValues={INITIAL_ADDRESS_VALUES}
                                                  onSubmit={handleSaveAddress}
                                                  setShowPopover={setShowPopover}
                                                  accountData={data}
                                                />
                                              }
                                            >
                                              <AddLocationLink
                                                href="#settings"
                                                ref={addLocationRef}
                                                className="btn btn-link p-0"
                                                onClick={(e) => {
                                                  e.preventDefault()
                                                  handleOpenAddressPopover()
                                                }}
                                              >
                                                {getLocalText('ADD_A_LOCATION', 'Add a location')}
                                              </AddLocationLink>
                                            </OverlayTrigger>
                                          </div>
                                        </div>
                                        <Select
                                          id="do-location"
                                          name="DeliveryLocationID"
                                          options={
                                            data?.LocationCollection?.map((x) => ({
                                              value: x.ID,
                                              label: x.Description
                                            })) || []
                                          }
                                          value={
                                            data?.LocationCollection?.find(
                                              (lc) => lc.ID === formik.values.DeliveryLocationID
                                            )
                                              ? {
                                                  value: formik.values.DeliveryLocationID,
                                                  label: data?.LocationCollection.find(
                                                    (lc) => lc.ID === formik.values.DeliveryLocationID
                                                  )?.Description
                                                }
                                              : null
                                          }
                                          onChange={(selectedOption: any) => {
                                            if (selectedOption) {
                                              formik.setFieldValue('DeliveryLocationID', selectedOption.value)
                                            }
                                          }}
                                          isClearable={false}
                                        />
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                              </SettingsSection>
                            )}
                          </>
                        )}
                        {data?.showBid && getSystemSetting('DISPLAY_BIDDING_PROFILE_SECTION') !== '0' && (
                          <SettingsSection>
                            <Row>
                              <Col>
                                <SectionTitle>{getLocalText('BIDDING_PROFILE', 'Bidding Profile')}</SectionTitle>
                              </Col>
                            </Row>
                            <Row>
                              <Col sm={12}>
                                <Form.Group className="form-section">
                                  <Form.Label>{getLocalText('MATCH_RESERVE', 'Match Reserve')}</Form.Label>
                                  <div className="checkbox-group">
                                    <Form.Check
                                      type="checkbox"
                                      id="mp-match-reserve"
                                      name="IsReserveMatch"
                                      custom
                                      checked={formik.values.IsReserveMatch}
                                      onChange={formik.handleChange}
                                      label={getLocalText(
                                        'MATCH_TO_RESERVE',
                                        'Match to reserve if maximum bid is greater than reserve price'
                                      )}
                                    />
                                  </div>
                                </Form.Group>
                              </Col>
                            </Row>
                          </SettingsSection>
                        )}
                        {data?.BuyerList && data.BuyerList.length > 1 && !isInRule('OVE Colisting') && (
                          <SettingsSection>
                            <Row>
                              <Col>
                                <SectionTitle>{getLocalText('DEFAULT_BUYER', 'Default Buyer')}</SectionTitle>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <Form.Group className="form-section">
                                  <Form.Row>
                                    <Form.Group as={Col} sm={6} md={4}>
                                      <Form.Label htmlFor="mp-question-1">
                                        {getLocalText('DEFAULT_BUYER', 'Default Buyer')}
                                      </Form.Label>
                                      <Select
                                        id="mp-question-1"
                                        name="CurBuyer"
                                        options={buyerOptions}
                                        value={
                                          buyerOptions.find((option) => option.value === formik.values.CurBuyer) || null
                                        }
                                        onChange={(selectedOption: any) => {
                                          if (selectedOption) {
                                            formik.setFieldValue('CurBuyer', selectedOption.value)
                                          }
                                        }}
                                        placeholder={getLocalText('SELECT_DEFAULT_BUYER', 'Select Default Buyer')}
                                        isClearable={false}
                                      />
                                      {formik.touched.CurBuyer && formik.errors.CurBuyer && (
                                        <span className="has-error">{formik.errors.CurBuyer}</span>
                                      )}
                                      {buyerError && <span className="has-error">{buyerError}</span>}
                                    </Form.Group>
                                  </Form.Row>
                                </Form.Group>
                              </Col>
                            </Row>
                          </SettingsSection>
                        )}
                        {data?.accountList && data.accountList.length > 1 && (
                          <SettingsSection>
                            <Row>
                              <Col>
                                <SectionTitle>
                                  {getLocalText('DEFAULT_BUYER_ACCOUNT', 'Default Buyer Account')}
                                </SectionTitle>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <Form.Group className="form-section">
                                  <Form.Row>
                                    <Form.Group as={Col} sm={6}>
                                      <Form.Label htmlFor="mp-account-select">
                                        {getLocalText('DEFAULT_ACCOUNT_NUMBER', 'Default Account Number')}
                                      </Form.Label>
                                      <Select
                                        id="mp-account-select"
                                        name="curAccount"
                                        options={data?.accountList.map((x) => ({ value: x.ID, label: x.Description }))}
                                        value={
                                          data?.accountList.find((acc) => acc.ID === formik.values.curAccount)
                                            ? {
                                                value: formik.values.curAccount,
                                                label: data?.accountList.find(
                                                  (acc) => acc.ID === formik.values.curAccount
                                                )?.Description
                                              }
                                            : null
                                        }
                                        onChange={(selectedOption: any) => {
                                          if (selectedOption) {
                                            formik.setFieldValue('curAccount', selectedOption.value)
                                          }
                                        }}
                                        isClearable={false}
                                      />
                                    </Form.Group>
                                  </Form.Row>
                                </Form.Group>
                              </Col>
                            </Row>
                          </SettingsSection>
                        )}
                        {showAuctionAcessRegistration.Value === '1' && (
                          <SettingsSection>
                            <Row>
                              <Col>
                                <SectionTitle>
                                  {getLocalText('AUCTION_ACCESS_REGISTRATION_ACCOUNT', 'AuctionACCESS Registration')}
                                </SectionTitle>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <Form.Group className="form-section">
                                  <Form.Row>
                                    <Form.Group as={Col} sm={9}>
                                      <Form.Label htmlFor="mp-auction-access">
                                        {getLocalText(
                                          'AUCTION_ACCESS_REGISTRATION_LABEL',
                                          'Would you like to buy on RPM? Click to register your AuctionACCESS id with RPM'
                                        )}
                                      </Form.Label>
                                    </Form.Group>
                                  </Form.Row>
                                  <Form.Group>
                                    <a
                                      href="#"
                                      className="click-text click-only"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        handleLaunchAuctionAccess()
                                      }}
                                    >
                                      {getLocalText('AUCTION_ACCESS_REGISTRATION_LINK', 'Register Auction Access')}
                                    </a>
                                  </Form.Group>
                                </Form.Group>
                              </Col>
                            </Row>
                          </SettingsSection>
                        )}
                        {showSiteNotificationEmails?.Value === '1' && isInRule('PAGE_RDP_SRP') && (
                          <SettingsSection>
                            <Row>
                              <Col>
                                <SectionTitle>Add Additional Email Notifications</SectionTitle>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <Form.Group className="form-section">
                                  <EmailConfirmation name="PurchaseEmails" label="PURCHASE CONFIRMATION" />
                                  <EmailConfirmation name="SoldEmails" label="GATE RELEASE CONFIRMATION" />
                                </Form.Group>
                              </Col>
                            </Row>
                          </SettingsSection>
                        )}
                        <SettingsSection>
                          <Row>
                            <Col>
                              <SectionTitle>{getLocalText('CARFAX', 'CARFAX')}</SectionTitle>
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                              <Form.Group className="form-section">
                                <p>
                                  <OverlayTrigger
                                    trigger={!isCarfaxLoggedIn ? ['hover', 'focus'] : []}
                                    placement="top"
                                    overlay={
                                      <Tooltip id="carfax-tooltip">You have not logged in to Carfax yet</Tooltip>
                                    }
                                  >
                                    <CarfaxSignOutLink
                                      href="#"
                                      $isLoggedIn={isCarfaxLoggedIn}
                                      onClick={isCarfaxLoggedIn ? handleCarfaxLogout : (e) => e.preventDefault()}
                                    >
                                      <span>{getLocalText('CARFAX_SIGN_OUT', 'Sign out')}</span>
                                    </CarfaxSignOutLink>
                                  </OverlayTrigger>{' '}
                                  {getLocalText(
                                    'CARFAX_SIGN_OUT_MESSAGE',
                                    'of CARFAX. No CARFAX features are available after signing out.'
                                  )}
                                </p>
                              </Form.Group>
                            </Col>
                          </Row>
                        </SettingsSection>
                        <GroupButtonStyled>
                          <Form.Row>
                            <CancelButton>
                              <LinkBaseStyled to={!userClaims.Username ? '/' : '/'}>
                                <FormattedMessage id={MultiLanguageCode.CANCEL_BUTTON} defaultMessage="Cancel" />
                              </LinkBaseStyled>
                            </CancelButton>
                            <UpdateButton type="submit" size="sm" disabled={isLoading || isSaving}>
                              <FormattedMessage id={MultiLanguageCode.SUBMIT} defaultMessage="Update" />
                            </UpdateButton>
                          </Form.Row>
                        </GroupButtonStyled>
                      </Col>
                    </Container>
                  </MainContainer>
                </Form>
              )
            }}
          </Formik>
        )}
      </AccountSettingStyled>
    </Layout>
  )
}
